import { Component, ElementRef, Inject, OnDestroy, OnInit, PLATFORM_ID, QueryList, ViewChildren, afterNextRender, computed, inject, signal, } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { SignalsStoreService } from '../shared/signals-store.service';
import { SidebarComponent } from '../shared/sidebar/sidebar.component';
import { ProductsService } from '../product/products.service';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { toObservable } from '@angular/core/rxjs-interop';
import { auditTime, bufferWhen, filter, map, mergeMap, of, scan, startWith, Subject, switchMap, tap, timer } from 'rxjs';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { StockService } from '../stock/stock.service';
import { MEDIUM } from "../../scss/responsive/responsive";
import { HeaderService } from '../shared/header/header.service';
import { ResolutionService } from '../shared/resolution.service';
import { EmptyMessageComponent } from '../shared/empty-message/empty-message.component';
import { ShopService } from './shop.service';
import { MenuService } from '../shared/menu.service';
import { environment } from '../../environments/environment';
import { ProductCardV2Component } from '../shared/product-card-v2/product-card-v2.component';
import { Product, ProductResume } from '../product/product.types';
import { ActivatedRoute, Router } from '@angular/router';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { SidebarService } from '../shared/sidebar/sidebar.service';
import { DataLayerService } from '../shared/data-layer/data-layer.service';
import { formatStringForURL } from '../shared/common/utils';

@Component({
  selector: 'app-shop',
  templateUrl: './shop.component.html',
  styleUrl: './shop.component.scss',
  imports: [
    CommonModule,
    SidebarComponent,
    NgbModule,
    MatProgressBarModule,
    EmptyMessageComponent,
    ProductCardV2Component,
    MatFormFieldModule,
    MatChipsModule,
  ]
})
export class ShopComponent implements OnInit, OnDestroy {

  @ViewChildren('subcategory')
  subcategories!: QueryList<ElementRef>;

  @ViewChildren('subcategorySon')
  subcategoriesSon!: QueryList<ElementRef>;

  #productsService = inject(ProductsService);
  #headerService = inject(HeaderService);
  #resolutionService = inject(ResolutionService);
  #menuService = inject(MenuService);
  #shopService = inject(ShopService);
  sidebarService = inject(SidebarService);
  private dataLayerService = inject(DataLayerService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private location = inject(Location);

  categoryBanner = computed(() => this.#shopService.categoryBanner());

  signalsStore = inject(SignalsStoreService);
  stockService = inject(StockService);

  isSidebarOpen = this.signalsStore.isSidebarOpen;
  hasSession = this.signalsStore.hasSession;
  selectedCategory = this.signalsStore.selectedCategory;

  isCollapseButtonHovered = signal(false);
  lastSubcategory: string | null = null;
  lastCategory: string | null = null;
  productsSignal = computed(() => this.#setUpProductsSignal());
  categoriesSignal: any = computed(() => this.#productsService.productsSignal());
  marketStatus = computed(() => this.signalsStore.marketStatus());
  closedMarket = computed(() => {
    const im = this.isMobile();
    const ms = this.marketStatus();
    return {
      title: !ms.isOpen && im ?
        `Our Online Farmer's Market Opens ${ms.openingDay.toUpperCase()} Morning!` :
        `Our Online Farmer's Market Opens ${ms.openingDay.toUpperCase()} Morning!`,
      legend: `We're busy sourcing next week's availability! Check back Friday to begin shopping.`
    };
  })

  isContentLoading = computed(() => this.stockService.isLoading() || this.#productsService.isLoading());
  isContentLoaded = computed(() => this.stockService.isLoaded() || this.#productsService.isLoaded());

  isMobile = computed(() => this.#resolutionService.isMobile());

  emptyMessage = 'No products were found.';
  hasGlobalMessages = computed(() => !!this.signalsStore.globalMessages().length);

  //#region flows config
  displayCategoryName = signal(environment.config.flows.shop.displayCategoryName);
  //#endregion

  productDetails = signal<Map<number, Product>>(new Map()); // Cache of product details
  #visibleProducts$ = new Subject<number[]>(); // IDs of products that are visible in the viewport

  filters = computed(() => this.signalsStore.getFilters());

  showFiltersTags = computed(() => this.isMobile() && this.sidebarService.filterTags().length);

  showMoreItemTags = signal(false);

  constructor(@Inject(PLATFORM_ID) private platformId: any) {

    toObservable(this.signalsStore.globalFilter)
      .subscribe(() => this.#productsService.filterBySidebarChange());

    toObservable(this.sidebarService.filterTags)
      .subscribe(() => this.#validateScrollTags());

    afterNextRender(() => {
      const isMobile = window.innerWidth <= MEDIUM;
      if (isMobile) this.signalsStore.isSidebarOpen.set(false);
    });
  }

  ngOnInit(): void {

    this.signalsStore.setIsInShopComponent(true);

    if (this.signalsStore.filterByProducer()) {
      setTimeout(() => {
        this.#headerService.producerRedirection();
      }, 500);
    }

    this.#setupBatchRequests();
  }

  ngOnDestroy(): void {
    this.#headerService.clearFilters(true, false);
    this.signalsStore.setIsInShopComponent(false);
  }

  scrollIntoView() {
    const selectedProductId = localStorage.getItem('selectedProductId');
    if (selectedProductId != null) {
      this.#gotoSelectedProduct(selectedProductId);
    } else {
      this.#handleRouteChanges();
    }
  }


  #gotoSelectedProduct(selectedProductId: string) {
    setTimeout(() => {
      if (selectedProductId != null) {
        const scrollToTop = document.getElementById(selectedProductId);
        if (scrollToTop) {

          const parent = document.getElementById('layoutShopMain');
          parent?.scrollTo({
            top: (scrollToTop.offsetTop - 100),
            left: 0,
            behavior: 'instant'
          });

          localStorage.removeItem('selectedProductId');
        }
      }
    }, 500);
  }

  toggleSidebar() {
    this.signalsStore.isSidebarOpen.update((currentValue) => !currentValue);
  }

  toggleCollapseBarHover(hovered: boolean) {
    this.isCollapseButtonHovered.set(hovered);
  }

  scrollToTop(): void {
    const scrollToTop = document.getElementById('scroll-to-top-ref');
    if (scrollToTop) {
      scrollToTop.scrollIntoView({ block: 'start' });
    }
  }

  getCategoryBanner(categoryId: number): { banner: string, url: string } | null {

    const category = this.#menuService.menu().find(x => x.id === categoryId);

    const banner = category?.banners?.[this.isMobile() ? 'mobile' : 'desktop'];

    if (!banner) return null;

    return {
      banner,
      url: category.banners.url
    };
  }

  getSubCategoryBanner(categoryId: number, subCategoryId: number): { banner: string, url: string } | null {

    const category = this.#menuService.menu().find(x => x.id === categoryId);

    if (!category) return null;

    const subcategory = category.subCategories
      .find((x: any) => x.id === subCategoryId);

    const banner = subcategory?.banners?.[this.isMobile() ? 'mobile' : 'desktop'];

    if (!banner) return null;

    return {
      banner,
      url: subcategory.banners.url
    };
  }

  #setupBatchRequests() {
    const noActivity$ = this.#visibleProducts$.pipe(
      switchMap(() => timer(100).pipe(map(() => true))), // Si no hay nuevos valores en 500ms → inactividad
      startWith(false) // Al inicio hay actividad
    );

    this.#visibleProducts$.pipe(
      bufferWhen(() => noActivity$.pipe(filter(inactive => inactive))), // Espera a que haya inactividad
      filter(productGroups => productGroups.length > 0), // Evita procesar arrays vacíos
      mergeMap(productGroups => productGroups.flat()), // 🔄 Aplana el array → number[]
      scan((acc: number[], productId: number) => [...new Set([...acc, productId])], []), // 📌 Acumula productos únicos
      auditTime(500), // ⏳ Espera estabilidad antes de hacer la petición
      switchMap(productIds => {
        // console.log("📌 Productos acumulados para fetch:", productIds);
        const idsToFetch = productIds.filter(id => !this.productDetails().has(id));
        // console.log("⚠️ Productos a buscar:", idsToFetch);
        return idsToFetch.length > 0 ? this.#productsService.getProductsDetailsByIds(idsToFetch) : of({});
      })
    ).subscribe(details => {
      this.productDetails.update(prev => {
        // console.log("✅ Detalles recibidos:", details);
        const updatedMap = new Map(prev); // Clonar el Map existente
        Object.entries(details).forEach(([index, detail]) => {
          const productDetail: Product = detail as Product;
          updatedMap.set(Number(productDetail.id), productDetail as Product); // Convertir id a número y agregarlo
        });
        return updatedMap;
      });
    });
  }

  onProductCardReady(productId: number) {
    if (this.productDetails().has(productId)) return;
    else this.#visibleProducts$.next([productId]);
  }

  onProductCardHover(productId: number) {
    if (this.productDetails().has(productId)) return;
    else this.#visibleProducts$.next([productId]);
  }

  removeChipFilter(key: string) {
    if (key && this.filters().has(key)) {
      const type = this.filters().get(key)?.type;
      if (!type) return;

      this.signalsStore.setFilterChipRemoved(key);
    }
  }

  #handleRouteChanges() {
    setTimeout(() => {
      const category = this.activatedRoute.snapshot.paramMap.get('category');
      const subcategory = this.activatedRoute.snapshot.paramMap.get('subcategory');
      const subcategorySon = this.activatedRoute.snapshot.paramMap.get('slug');
      const routeArray = [];
      if (category) routeArray.push(category);
      if (subcategory) routeArray.push(subcategory);
      if (subcategorySon) routeArray.push(subcategorySon);

      const anchorLink = `/shop/${routeArray.join('/')}`;

      const element: any = document.querySelector(`[shop-data-anchor-link="${anchorLink}"]`);
      if (!element) {
        return
      };
      const parent = document.getElementById('layoutShopMain');
      parent?.scrollTo({
        top: (element.offsetTop),
        left: 0,
        behavior: 'instant'
      });
    }, 1);
  }

  changeAnchorLink(anchorLink: string) {
    this.location.replaceState(anchorLink);
  }

  onScroll(event: any) {
    // Nothing products in shop
    if (!this.subcategories?.length)
      return;

    const contentY = document
      .getElementsByClassName('layout-shop__main')[0]
      ?.getBoundingClientRect().y ?? 0;

    if (!contentY)
      return;

    // Combine both arrays with their type identifier
    const allElements = [
      ...this.subcategories.toArray().map(el => ({ el, isSon: false })),
      ...this.subcategoriesSon.toArray().map(el => ({ el, isSon: true }))
    ];

    // Calculate distances for all elements
    const elementsWithDistance = allElements.map(({ el, isSon }) => {
      const diffY = el.nativeElement.getBoundingClientRect().y - contentY;
      return { el, diffY, isSon };
    });

    let element: ElementRef<any> | undefined;
    const isBottom = event.srcElement.scrollHeight - event.srcElement.scrollTop <= event.srcElement.clientHeight + 5;

    if (isBottom) {
      // At bottom: get the last visible element (highest diffY that's still negative or slightly positive)
      const found = elementsWithDistance.reduce((prev, curr) => 
        curr.diffY > prev.diffY ? curr : prev
      );
      element = found?.el;
    }
    else {
      // Find the element closest to the top of viewport (within reasonable range)
      const THRESHOLD = 150; // Simplified single threshold
      
      // First, try to find an element just below or at the top (diffY between -50 and THRESHOLD)
      let found = elementsWithDistance.find(item => item.diffY >= -50 && item.diffY <= THRESHOLD);

      // If not found, get the last element that has passed the viewport top (highest negative diffY)
      if (!found) {
        const passedElements = elementsWithDistance.filter(item => item.diffY < 0);
        if (passedElements.length > 0) {
          // Get the one with the smallest absolute value (closest to 0, meaning most recent)
          found = passedElements.reduce((prev, curr) => 
            curr.diffY > prev.diffY ? curr : prev
          );
        }
      }

      element = found?.el;
    }

    if (!element)
      return;

    // Get the category and subcategory attributes
    const categoryId = +element.nativeElement.getAttribute('category-id');
    const subCategoryId = +element.nativeElement.getAttribute('sub-category-id');
    const subCategorySonId = +element.nativeElement.getAttribute('sub-category-son-id');

    // If yet not change of category, don't do nothing.
    if (this.signalsStore.categoryInViewPort()?.categoryId === categoryId &&
      this.signalsStore.categoryInViewPort()?.subCategoryId === subCategoryId &&
      this.signalsStore.categoryInViewPort()?.subCategorySonId === subCategorySonId)
      return;

    // Emit the new visible category.
    this.signalsStore
      .categoryInViewPort
      .set({
        categoryId,
        subCategoryId,
        subCategorySonId
      });

    const viewportAnchorLink = element.nativeElement.getAttribute('shop-data-anchor-link');
    if (viewportAnchorLink) this.changeAnchorLink(viewportAnchorLink);
  }

  #setupProductAnchorLinks(product: any) {
    const anchorLinkArray = ['shop'];

    if (product.category.name) {
      anchorLinkArray.push(formatStringForURL(product.category.name));
      const categoryAnchorLink = `/${anchorLinkArray.join('/')}`;
      product.categoryAnchorLink = categoryAnchorLink;
    }

    if (product.subcategory?.name) {
      anchorLinkArray.push(formatStringForURL(product.subcategory.name));
      const subcategoryAnchorLink = `/${anchorLinkArray.join('/')}`;
      product.subcategoryAnchorLink = subcategoryAnchorLink;
    }

    if (product.subcategorySon?.name) {
      anchorLinkArray.push(formatStringForURL(product.subcategorySon.name));
      const subcategorySonAnchorLink = `/${anchorLinkArray.join('/')}`;
      product.subcategorySonAnchorLink = subcategorySonAnchorLink;
    }
  }

  #getCategoryBanner(categoryId: number, subCategoryId?: number, subCategorySonId?: number) {

    let item = this.#menuService.menu()
      .find(x => x.id === categoryId);

    if (subCategoryId)
      item = item?.subCategories.find((x: any) => x.id === subCategoryId);

    if (subCategorySonId)
      item = item?.subCategories.find((x: any) => x.id === subCategorySonId);

    return this.#validateBanner(item);
  }

  #validateBanner(item: any): { banner: string, url: string, show: boolean } {

    const banner = item?.banners[this.isMobile() ? 'mobile' : 'desktop'];

    if (!banner)
      return { banner: '', url: '', show: true };

    return {
      banner,
      url: item.banners.url,
      show: true
    };
  }

  #setUpProductsSignal() {
    const products = this.#productsService.productsSignal();

    let lastCategory: number;
    let lastSubcategory: number;
    let lastSubcategorySon: number;

    if (!products.length) return [];
    this.#gotoSelectedProduct(localStorage.getItem('selectedProductId') || '');

    return products.map((product: any) => {

      product.subCategorySonInView = null;
      product.categoryInView = null;
      product.subCategoryInView = null;

      let categoryId = 0,
        subCategoryId = 0,
        subCategorySonId = 0;

      if (product.category) {
        categoryId = +product.category.id;
        if (categoryId !== lastCategory) {
          product.categoryInView = this.#getCategoryBanner(categoryId);
          lastCategory = categoryId;
        }
      }

      if (product.subcategory) {

        subCategoryId = +product.subcategory.id;
        if (subCategoryId !== lastSubcategory) {
          product.subCategoryInView = this.#getCategoryBanner(categoryId, subCategoryId);
          lastSubcategory = subCategoryId;
        }
      }

      if (product.subcategorySon) {
        subCategorySonId = +product.subcategorySon.id;
        if (subCategorySonId !== lastSubcategorySon) {
          product.subCategorySonInView = this.#getCategoryBanner(categoryId, subCategoryId, subCategorySonId);
          lastSubcategorySon = subCategorySonId;
        }
      }

      this.#setupProductAnchorLinks(product)

      return product as ProductResume;
    }) as ProductResume[];
  }

  #validateScrollTags() {
    setTimeout(() => {
      const ele = document.getElementById('filterTags');
      if (!ele) return;
      const { clientHeight, scrollHeight } = ele;
      this.showMoreItemTags.set(scrollHeight > clientHeight);
    }, 500);
  }

  onBannerClicked(url: string, banner_id: string) {
    this.dataLayerService.trackBannerClickedEvent({
      banner_id,
      banner_link: url || 'no-link'
    });

    if (!url) return;

    setTimeout(() => window.open(url, '_blank'), 100);
  }
}
